<script setup lang="ts">
const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { field } = useInlineBlockEditor(props.id);
const route = useRoute();
const injectedReportUserId = inject<{ value?: string } | null>("reportUserId", null);

const title = field<string>("title", "Download your report");
const description = field<string>("description", "");
const buttonText = field<string>("buttonText", "Download PDF");
const icon = field<string>("icon", "lucide:download");
const automationPath = field<string>("automationPath", "");
const reportPath = field<string>("reportPath", "/data/<userId>/reports/report.pdf");
const fileName = field<string>("fileName", "");
const editorCandidateId = computed(() =>
  injectedReportUserId?.value
  ?? (typeof route.query.user === "string" ? route.query.user : null),
);
</script>

<template>
  <EditorSection :title="t('appearance')" icon="lucide:panel-top" accent="content">
    <EditorField :label="t('titleLabel')">
      <Input v-model="title" class="h-8 rounded-sm bg-muted text-[13px]" />
    </EditorField>
    <EditorField :label="t('descriptionLabel')">
      <Textarea v-model="description" class="min-h-20 rounded-sm bg-muted text-[13px]" />
    </EditorField>
    <div class="grid grid-cols-2 gap-2">
      <EditorField :label="t('buttonTextLabel')">
        <Input v-model="buttonText" class="h-8 rounded-sm bg-muted text-[13px]" />
      </EditorField>
      <EditorField :label="t('iconLabel')">
        <Input
          v-model="icon"
          class="h-8 rounded-sm bg-muted font-mono text-[13px]"
          placeholder="lucide:download"
        />
      </EditorField>
    </div>
  </EditorSection>

  <EditorSection
    :title="t('behavior')"
    icon="lucide:workflow"
    accent="content"
    :default-open="false"
  >
    <EditorField :label="t('automationPathLabel')" :hint="t('automationPathHint')">
      <EditorPathInput
        v-model="automationPath"
        mode="existing-file"
        :scopes="['site']"
        content-type-filter="automation"
        placeholder="/automations/generate-report"
        aria-label="PDF generation automation path"
      />
    </EditorField>
    <EditorField :label="t('reportPathLabel')" :hint="t('reportPathHint')">
      <EditorPathInput
        v-model="reportPath"
        mode="output-target"
        :scopes="['user', 'system', 'site']"
        default-scope="user"
        :user-id="editorCandidateId"
        placeholder="/data/<userId>/reports/report.pdf"
        aria-label="Generated PDF report path"
      />
    </EditorField>
    <EditorField :label="t('fileNameLabel')" :hint="t('fileNameHint')">
      <Input
        v-model="fileName"
        class="h-8 rounded-sm bg-muted text-[13px]"
        placeholder="report.pdf"
      />
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  appearance: Appearance
  behavior: Report download
  titleLabel: Title
  descriptionLabel: Description
  buttonTextLabel: Button text
  iconLabel: Icon
  automationPathLabel: Automation path
  automationPathHint: Optional. Run this automation and wait for it to complete before downloading.
  reportPathLabel: Generated PDF path
  reportPathHint: The output path configured on the Generate PDF Report node.
  fileNameLabel: Download filename
  fileNameHint: Optional. Defaults to the filename from the generated PDF path.
</i18n>
